import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { Flex, Typo, Avatar, SimpleButton, theme } from '../../../ui';
import { priceFormater } from '../../../helpers/formatNumbers';
import { media } from '../../../utils/media';
import { TOAST_INFO } from '../../../utils/constants';
import coursioLogo from '../../../ui/assets/coursio-logo.svg';

const SidebarWrapper = styled.div`
  width: 340px;
  flex-shrink: 0;
  position: sticky;
  top: 72px;

  ${media.lessThan('tablet')} {
    width: 100%;
    position: static;
  }
`;

const Card = styled.div`
  background: ${theme.colors.NEU_0};
  border-radius: 16px;
  box-shadow: ${theme.shadows.BOX_SHADOW_SMALL};
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - ${theme.headerSize} - 72px);
`;

const Header = styled(Flex)`
  padding: 20px 24px;
  border-bottom: 1px solid ${theme.colors.NEU_100};
`;

const CountText = styled.span`
  color: ${theme.colors.NEU_400};
  font-weight: 400;
`;

const Body = styled.div`
  flex: 1;
  padding: 16px 24px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const EmptyState = styled(Flex)`
  flex: 1;
  padding: 40px 0;
`;

const EmptyLogo = styled.img`
  width: 40px;
  height: 40px;
  opacity: 0.3;
  filter: grayscale(100%);
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
`;

const ItemAvatar = styled.div`
  flex-shrink: 0;
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const ItemName = styled(Typo.SmallText)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`;

const Footer = styled.div`
  border-top: 1px solid ${theme.colors.NEU_100};
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ActionRow = styled(Flex)`
  padding-top: 8px;
`;

const getImageUrl = (coverImage) => (Array.isArray(coverImage) ? coverImage[0]?.url : coverImage?.url);

const formatPrice = (price, hideVAT) => {
  const value = hideVAT ? price?.net : price?.gross;
  return value ?? 0;
};

const getCourseInfo = (course, hideVAT, t) => {
  const type = course.type === 2 ? t('coursioNetwork.catalog.package') : t('coursioNetwork.catalog.course');
  const price = formatPrice(course.price, hideVAT);
  const base = `https://app.coursio.com/store/${course.ownerSlug}`;
  const url = course.type === 2 ? `${base}/package/${course.slug}` : `${base}/${course.slug}`;
  return { type, price, url };
};

const handleExportCsv = (courses, hideVAT, currency, t) => {
  const header = 'Name,Owner,Type,Price,Currency,URL\n';
  const rows = courses
    .map((c) => {
      const { type, price, url } = getCourseInfo(c, hideVAT, t);
      return `"${c.name}","${c.ownerName}","${type}",${price},"${currency}","${url}"`;
    })
    .join('\n');
  const csv = header + rows;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = `coursio-network-selection-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(blobUrl);
};

const handleCopyText = async (courses, hideVAT, currency, t) => {
  const courseLines = courses
    .map((c) => {
      const { type, price, url } = getCourseInfo(c, hideVAT, t);
      return `• ${c.name} (${type}) — ${price} ${currency}\n  ${c.ownerName}\n  ${url}`;
    })
    .join('\n\n');
  const text = `Coursio Network — ${t('coursioNetwork.catalog.courseCount', { count: courses.length })}\n\n${courseLines}`;
  await navigator.clipboard.writeText(text);
  enqueueSnackbar(t('coursioNetwork.catalog.copiedToClipboard'), { variant: TOAST_INFO });
};

const handleSendEmail = (courses, hideVAT, currency, t) => {
  const courseLines = courses
    .map((c) => {
      const { type, price, url } = getCourseInfo(c, hideVAT, t);
      return `- ${c.name} (${type}) — ${price} ${currency}\n  ${url}`;
    })
    .join('\n');
  const body = t('coursioNetwork.catalog.emailBody', { courseLines });
  const subject = t('coursioNetwork.catalog.emailSubject', { count: courses.length });
  window.location.href = `mailto:info@coursio.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const SelectedCoursesSidebar = ({ selectedCourses, currency, hideVAT, onRemove, onClear }) => {
  const { t } = useTranslation();
  const hasSelection = selectedCourses.length > 0;

  return (
    <SidebarWrapper>
      <Card>
        <Header align='center' justify='space-between'>
          <Typo.TextTitle>
            {t('coursioNetwork.catalog.selectedCourses')} <CountText>({selectedCourses.length})</CountText>
          </Typo.TextTitle>
          <Flex gap='4'>
            <SimpleButton
              variant='ghost'
              icon='Copy'
              size='x-small'
              aria-label={t('coursioNetwork.catalog.copyToClipboardDesc')}
              disabled={!hasSelection}
              onClick={() => handleCopyText(selectedCourses, hideVAT, currency, t)}
            />
            <SimpleButton
              variant='ghost'
              icon='Export'
              size='x-small'
              aria-label={t('coursioNetwork.catalog.exportAsCsvDesc')}
              disabled={!hasSelection}
              onClick={() => handleExportCsv(selectedCourses, hideVAT, currency, t)}
            />
          </Flex>
        </Header>

        <Body>
          {!hasSelection ? (
            <EmptyState direction='column' align='center' justify='center' gap='12'>
              <EmptyLogo src={coursioLogo} alt='Coursio' />
              <Typo.SmallText color={theme.colors.NEU_400}>{t('coursioNetwork.catalog.noCoursesSelected')}</Typo.SmallText>
            </EmptyState>
          ) : (
            selectedCourses.map((course) => (
              <ItemRow key={course.id}>
                <ItemAvatar>
                  <Avatar name={course.name} size='small' url={getImageUrl(course.coverImage)} />
                </ItemAvatar>
                <ItemInfo>
                  <ItemName fontWeight='500'>{course.name}</ItemName>
                  <Typo.Info color={theme.colors.NEU_600}>{priceFormater(course.price, hideVAT, course.currency)}</Typo.Info>
                </ItemInfo>
                <SimpleButton variant='ghost' icon='Close' size='x-small' onClick={() => onRemove(course.id)} aria-label='remove' />
              </ItemRow>
            ))
          )}
        </Body>

        <Footer>
          <ActionRow gap='8' align='center' justify='space-between'>
            <SimpleButton bgColor={theme.colors.NEU_PRIMARY} variant='tertiary' size='small' onClick={onClear} disabled={!hasSelection}>
              {t('coursioNetwork.catalog.clear')}
            </SimpleButton>
            <SimpleButton icon='Send' size='small' disabled={!hasSelection} onClick={() => handleSendEmail(selectedCourses, hideVAT, currency, t)}>
              {t('coursioNetwork.catalog.sendViaEmail')}
            </SimpleButton>
          </ActionRow>
        </Footer>
      </Card>
    </SidebarWrapper>
  );
};

export default SelectedCoursesSidebar;
